"use client";

import { useState, useEffect } from "react";
import {
    Send,
    Eye,
    MousePointer,
    AlertCircle,
    DollarSign,
    ShoppingCart,
    TrendingUp,
    Users,
    List,
    CircleAlert,
    CircleX,
    CircleCheck,
    Pointer,
} from "lucide-react";

import { Skeleton } from "@material-ui/lab";
import {
    Badge,
    Card,
    CardContent,
    Typography,
    Divider,
} from "@material-ui/core";

import WhatsappCampaignService from "../../../../domain/services/marketing/whatsapp/whatsapp.campaign.service";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import datetimeProvider from "../../../../providers/datetime.provider";
import useRequest from "../../../../hooks/useRequest";
import SnackbarAlert from "../../../../components/shared/SnackbarAlert";
import emailCampaignsService from "../../../../domain/services/marketing/email.campaigns.service";
import segmentationTagService from "../../../../domain/services/marketing/segmentation.tag.service";

import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ComposedChart,
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
} from "recharts";

import Statistic from "../../../../components/shared/Statistic";
import Row from "../../../../components/shared/grid/Row";
import Col from "../../../../components/shared/grid/Col";
import { campaignStatus } from "../../../../domain/constant/campaign.email.constant";
import PageHeader from "../../../../components/shared/layout/pageHeader";
import SegmentationSummary from "../../../../components/marketing/shared/SegmentationSummary";

export default function WhatsappCampaignMetricsPage() {
    const { id } = useParams();
    const moment = datetimeProvider.momentInstance();

    const [findCampaignSettings, isFindCampaignSettings] = useRequest(
        WhatsappCampaignService.findCampaignSettings
    );
    const [findMetrics, isFindingMetrics] = useRequest(
        WhatsappCampaignService.findMetrics
    );
    const [findSalesMetrics, isFindingSalesMetrics] = useRequest(
        WhatsappCampaignService.findSalesMetrics
    );
    const [findProductSalesMetrics, isFindingProductSalesMetrics] = useRequest(
        WhatsappCampaignService.findProductSalesMetrics
    );
    const [findSalesByHoursMetrics, isFindSalesByHoursMetrics] = useRequest(
        WhatsappCampaignService.findSalesByHoursMetrics
    );
    const [findListsContactAmount] = useRequest(
        emailCampaignsService.findTotalContactsInLists
    );

    const [campaignDetails, setCampaignDetails] = useState({});
    const [metrics, setMetrics] = useState({});
    const [salesMetrics, setSalesMetrics] = useState({});
    const [productSalesMetrics, setProductSalesMetrics] = useState([]);
    const [hourlySales, setHourlySales] = useState([]);
    const [totalContacts, setTotalContacts] = useState(0);

    const [segmentationConfig, setSegmentationConfig] = useState({ included: [] });
    const [segmentationContactType, setSegmentationContactType] = useState('customer');
    const [segmentationTags, setSegmentationTags] = useState([]);
    const [segmentationContactsCount, setSegmentationContactsCount] = useState(null);
    const [loadingSegmentation, setLoadingSegmentation] = useState(false);


    useEffect(() => {
        getCampaignSettings(id);
    }, []);

    const getCampaignSettings = async (campaignId) => {
        if (!campaignId) return;

        getMessageMetrics(campaignId);
        getSalesMetrics(campaignId);
        getProductSalesMetrics(campaignId);
        getSalesByHours(campaignId);
        loadSegmentation(campaignId);

        const campaignSettings = await findCampaignSettings(campaignId).catch(
            () => SnackbarAlert.error("Erro ao buscar detalhes da campanha.")
        );

        if (!campaignSettings?.id) return;

        setCampaignDetails(campaignSettings);

        const listIds = campaignSettings.lists.map((l) => l.id);
        getContactAmount(campaignSettings, listIds);
    };

    const getCountContactsFromSegmentation = async (config, segmentationType) => {

        const res = await segmentationTagService
            .countContacts(segmentationType, config, 'whatsapp')
            .catch(() => null);

        setSegmentationContactsCount(res?.data?.total ?? 0);
    }

    const getSegmentationTags = async (config, segmentationType) => {

        const res = await segmentationTagService
            .findAll(segmentationType, 'whatsapp')
            .catch(() => ({ data: [] }));

        setSegmentationTags(res?.data || []);
        await getCountContactsFromSegmentation(config, segmentationType);
    }

    const loadSegmentation = async (campaignId) => {

        if (!campaignId) return;

        setLoadingSegmentation(true);

        try {
            const res = await segmentationTagService.findByCampaignIdAndChannelType(campaignId, 'whatsapp');
            const payload = res?.data || {};
            const segmentationType = payload.contactType;
            const config = payload.segmentationConfig || {};

            setSegmentationContactType(segmentationType);
            setSegmentationConfig({ included: config.included || [] });

            await getSegmentationTags(config, segmentationType);

        } catch (err) {
            setSegmentationConfig({ included: [] });
            setSegmentationTags([]);
            setSegmentationContactsCount(null);
        } finally {
            setLoadingSegmentation(false);
        }
    };

    const getContactAmount = (campaignSettings, selectedLists = []) => {

        if (!selectedLists.length) {
            setTotalContacts(0);
            return;
        }


        findListsContactAmount(selectedLists)
            .then((listAmounts) => {

                const updatedLists = campaignSettings.lists.map((list) => {
                    const amount = listAmounts.find((l) => l.id === list.id);
                    return {
                        ...list,
                        totalContacts: parseInt(amount?.totalContacts || 0)
                    };
                });

                setCampaignDetails({ ...campaignSettings, lists: updatedLists });

                const total = listAmounts.reduce(
                    (sum, curr) => sum + parseInt(curr.totalContacts),
                    0
                );
                setTotalContacts(total);
            })
            .catch(() => setTotalContacts(0));
    };

    const getMessageMetrics = async (campaignId) =>
        findMetrics({ campaignId })
            .then(data => setMetrics({ ...(data?.results || {}) }))
            .catch(() =>
                SnackbarAlert.error("Erro ao buscar métricas.")
            );

    const getSalesMetrics = async (campaignId) =>
        findSalesMetrics({ campaignId })
            .then(data => setSalesMetrics({ ...(data?.results || {}) }))
            .catch(() =>
                SnackbarAlert.error("Erro ao buscar métricas de vendas.")
            );

    const getProductSalesMetrics = async (campaignId) =>
        findProductSalesMetrics({ campaignId })
            .then(data => setProductSalesMetrics(data || []))
            .catch(() =>
                SnackbarAlert.error("Erro ao buscar métricas de vendas.")
            );

    const getSalesByHours = async (campaignId) =>
        findSalesByHoursMetrics({ campaignId })
            .then(data => {

                const apiHours = (data || []).map(item => ({
                    hour: Number(item.hour),
                    sales: Number(item.sales || 0),
                    revenue: Number(item.revenue || 0)
                }));

                const fullTimeline = Array.from({ length: 24 }, (_, h) => {
                    const found = apiHours.find(item => item.hour === h);
                    return {
                        hour: `${String(h).padStart(2, "0")}h`,
                        sales: found?.sales ?? 0,
                        revenue: found?.revenue ?? 0
                    };
                });

                setHourlySales(fullTimeline);
            })
            .catch(() =>
                SnackbarAlert.error("Erro ao buscar métricas de vendas por horário.")
            );


    const bestPeriod = (() => {

        if (!hourlySales || hourlySales.length === 0)
            return { start: "--", end: "--", total: 0 };

        if (hourlySales.length === 1) {
            const h = hourlySales[0];
            return { start: h.hour, end: h.hour, total: h.sales };
        }

        let maxSum = -1;
        let bestStart = 0;

        for (let i = 0; i < hourlySales.length - 1; i++) {
            const sum = hourlySales[i].sales + hourlySales[i + 1].sales;

            if (sum > maxSum) {
                maxSum = sum;
                bestStart = i;
            }
        }

        return {
            start: hourlySales[bestStart]?.hour,
            end: hourlySales[bestStart + 1]?.hour,
            total: maxSum
        };
    })();


    const conversionRates = {
        deliveryRate: metrics.totalDelivered ? ((metrics.totalDelivered / metrics.totalSent) * 100).toFixed(1) : 0,
        viewRate: metrics.totalRead ? ((metrics.totalRead / metrics.totalSent) * 100).toFixed(1) : 0,
        clickRate: metrics.totalClicked ? ((metrics.totalClicked / metrics.totalSent) * 100).toFixed(1) : 0,
        undeliveredRate: metrics.totalUndelivered ? ((metrics.totalUndelivered / metrics.totalSent) * 100).toFixed(1) : 0,
        failedRate: metrics.totalFailed ? ((metrics.totalFailed / metrics.totalSent) * 100).toFixed(1) : 0
    };

    const getProductChartData = () => {

        if (!Array.isArray(productSalesMetrics) || productSalesMetrics.length === 0)
            return [];

        const PRODUCT_TYPES = [
            { key: "start", name: "Pacote Start", color: "#765cf7d8" },
            { key: "premium", name: "Pacote Premium", color: "#10B981" },
            { key: "infinity", name: "Pacote Infinity", color: "#4245f9ff" }
        ];

        return PRODUCT_TYPES.map(pt => {
            const found = (productSalesMetrics || []).find(p => p.key?.toLowerCase() === pt.key.toLowerCase());

            return {
                name: pt.name,
                value: parseFloat(found?.totalRevenue || 0),
                vendas: parseInt(found?.totalPurchases || 0),
                color: pt.color
            };
        });
    };

    const hasSegmentation = () =>
        (segmentationConfig.included || []).length > 0;

    const renderListsCard = () => {
        if (!(campaignDetails.lists || []).length) return null;

        return (
            <Card>
                <CardContent className="space-y-4">

                    <div>
                        <p className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                            <List className="h-4 w-4" />
                            Listas de Contatos
                        </p>

                        <div className="grid gap-1 md:grid-cols-1" style={{ marginTop: "20px" }}>
                            {(campaignDetails.lists || []).map((list, index) => (
                                <div key={list.id}>
                                    <p className="text-sm font-medium text-gray-900">{`${index + 1} - ${list.name}`}</p>
                                    <p className="text-xs  text-gray-500 flex items-center gap-1 mt-1">
                                        Contatos: {list.totalContacts?.toLocaleString("pt-BR")}
                                    </p>
                                    <Divider />
                                </div>
                            ))}
                        </div>

                        <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                            <Users className="h-4 w-4" />
                            <span>
                                Total:{" "}
                                <strong>{totalContacts?.toLocaleString("pt-BR")}</strong> contatos
                            </span>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    };

    const renderSegmentationCard = () => (
        <Card>
            <CardContent className="space-y-2">
                <SegmentationSummary
                    segmentationConfig={segmentationConfig}
                    contactType={segmentationContactType}
                    availableTags={segmentationTags}
                    contactsCount={segmentationContactsCount}
                    loading={loadingSegmentation}
                    title="Segmentação"
                />
            </CardContent>
        </Card>
    );

    if (isFindCampaignSettings) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-7xl mx-auto space-y-6">
                    <Skeleton className="h-12 w-64" />
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                        {[...Array(4)].map((_, i) => (
                            <Skeleton key={i} className="h-32" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    const getStatus = (status) =>
        campaignStatus[status] || campaignStatus.SCHEDULED

    const renderPieLabel = ({
        cx,
        cy,
        midAngle,
        innerRadius,
        outerRadius,
        percent,
        payload,
        value
    }) => {
        const RADIAN = Math.PI / 180;

        const radius = innerRadius + (outerRadius - innerRadius) * 0.65;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text
                x={x}
                y={y}
                fill="#111"
                textAnchor={x > cx ? "start" : "end"}
                dominantBaseline="central"
                style={{ fontSize: 11, fontWeight: 500 }}
            >
                {`${(percent * 100).toFixed(1)}%`}
                <tspan x={x} dy="1.2em" fontWeight="bold">
                    R$ {value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                </tspan>
            </text>
        );
    };

    const metricsData = [
        {
            title: 'Enviadas',
            value: parseFloat(metrics.totalSent || 0).toLocaleString("pt-BR"),
            prefix: <Send className="h-4 w-4 text-blue-600" />,
            loading: isFindingMetrics,
            titleClassName: "text-sm font-medium text-gray-700 flex items-center gap-2"
        },
        {
            title: 'Entregues',
            value: parseFloat(metrics.totalDelivered || 0).toLocaleString("pt-BR"),
            prefix: <CircleCheck className="h-4 w-4 text-green-600" />,
            suffix: metrics.totalDelivered > 0 && (
                <span className="ml-2 text-gray-600">
                    {conversionRates.deliveryRate}%
                </span>
            ),
            loading: isFindingMetrics,
            titleClassName: "text-sm font-medium text-gray-700 flex items-center gap-2"
        },
        {
            title: 'Visualizadas',
            value: parseFloat(metrics.totalRead || 0).toLocaleString("pt-BR"),
            prefix: <Eye className="h-4 w-4 text-purple-600" />,
            suffix: metrics.totalRead > 0 && (
                <span className="ml-2 text-gray-600">
                    {conversionRates.viewRate}%
                </span>
            ),
            loading: isFindingMetrics,
            titleClassName: "text-sm font-medium text-gray-700 flex items-center gap-2"
        },
        {
            title: 'Cliques',
            value: parseFloat(metrics.totalClicked || 0).toLocaleString("pt-BR"),
            prefix: <Pointer className="h-4 w-4 text-blue-600" />,
            suffix: metrics.totalClicked > 0 && (
                <span className="ml-2 text-gray-600">
                    {conversionRates.clickRate}%
                </span>
            ),
            loading: isFindingMetrics,
            titleClassName: "text-sm font-medium text-gray-700 flex items-center gap-2"
        },
        {
            title: 'Não Entregues',
            value: parseFloat(metrics.totalUndelivered || 0).toLocaleString("pt-BR"),
            prefix: <CircleAlert className="h-4 w-4 text-orange-600" />,
            suffix: metrics.totalUndelivered > 0 && (
                <span className="ml-2 text-gray-600">
                    {conversionRates.undeliveredRate}%
                </span>
            ),
            loading: isFindingMetrics,
            titleClassName: "text-sm font-medium text-gray-700 flex items-center gap-2"
        },
        {
            title: 'Falhas',
            value: parseFloat(metrics.totalFailed || 0).toLocaleString("pt-BR"),
            prefix: <CircleX className="h-4 w-4 text-red-600" />,
            suffix: metrics.totalFailed > 0 && (
                <span className="ml-2 text-gray-600">
                    {conversionRates.failedRate}%
                </span>
            ),
            loading: isFindingMetrics,
            titleClassName: "text-sm font-medium text-gray-700 flex items-center gap-2"
        },
    ];

    return (
        <div className="font-['SF_Pro'] p-6 bg-gray-50 min-h-screen">

            <PageHeader
                title="Métricas da Campanha"
                backButtonTo="/marketing/whatsapp/campaigns"
            />

            <div className="max-w-7xl mx-auto space-y-8">

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div>
                            <h1 className="text-lg text-gray-900">{campaignDetails.name}</h1>
                            <p className="text-sm text-gray-500 mt-1">
                                Enviada em{" "}
                                {moment(campaignDetails.scheduledAt).utc().format('DD/MM/YYYY HH:mm')}
                            </p>
                        </div>
                    </div>

                    <Badge
                        style={{
                            color: "#fff",
                            background: getStatus(campaignDetails.status).color,
                            padding: 4,
                            borderRadius: 4
                        }}
                    >
                        {getStatus(campaignDetails.status).label}
                    </Badge>
                </div>

                {hasSegmentation() ? renderSegmentationCard() : renderListsCard()}

                <h2 className="text-xl text-gray-900">Métricas de Envio</h2>

                <Row gutter={[24, 24]}>

                    {metricsData.map((metric) => (
                        <Col span={24} md={12} lg={8} xl={6}>
                            <Card className="transition-shadow hover:shadow-lg">
                                <CardContent className="p-4">
                                    <Statistic {...metric} />
                                </CardContent>
                            </Card>
                        </Col>
                    ))}

                </Row>

                <h2 className="text-xl text-gray-900">Métricas de Vendas</h2>

                <Row gutter={[24, 24]}>

                    <Col span={24} md={12} lg={8} xl={6}>
                        <Card className="transition-shadow hover:shadow-lg">
                            <CardContent className="p-4">
                                <Statistic
                                    title="Receita Total"
                                    loading={isFindingSalesMetrics}
                                    value={`R$ ${parseFloat(salesMetrics.totalRevenue)?.toLocaleString(
                                        "pt-BR",
                                        { minimumFractionDigits: 2 }
                                    )}`}
                                    titleClassName="text-sm font-medium text-gray-700 flex items-center gap-2"
                                    valueClassName="text-3xl font-bold text-green-700"
                                    prefix={<DollarSign className="h-4 w-4 text-green-600" />}
                                />
                            </CardContent>
                        </Card>
                    </Col>

                    <Col span={24} md={12} lg={8} xl={6}>
                        <Card className="transition-shadow hover:shadow-lg">
                            <CardContent className="p-4">
                                <Statistic
                                    title="Total de Vendas"
                                    loading={isFindingSalesMetrics}
                                    value={salesMetrics.totalPurchases}
                                    titleClassName="text-sm font-medium text-gray-700 flex items-center gap-2"
                                    valueClassName="text-3xl font-bold text-blue-700"
                                    prefix={<ShoppingCart className="h-4 w-4 text-blue-600" />}
                                />
                            </CardContent>
                        </Card>
                    </Col>

                    <Col span={24} md={12} lg={8} xl={6}>
                        <Card className="transition-shadow hover:shadow-lg">
                            <CardContent className="p-4">
                                <Statistic
                                    title="Investimento"
                                    loading={isFindingSalesMetrics}
                                    value={`R$ ${parseFloat(salesMetrics.totalInvestiment || 0).toLocaleString(
                                        "pt-BR",
                                        { minimumFractionDigits: 2 }
                                    )}`}
                                    titleClassName="text-sm font-medium text-gray-700 flex items-center gap-2"
                                    valueClassName="text-3xl font-bold text-orange-700"
                                    prefix={<TrendingUp className="h-4 w-4 text-orange-600" />}
                                />
                            </CardContent>
                        </Card>
                    </Col>

                    <Col span={24} md={12} lg={8} xl={6}>
                        <Card className="transition-shadow hover:shadow-lg">
                            <CardContent className="p-4">
                                <Statistic
                                    title="Ticket Médio"
                                    loading={isFindingSalesMetrics}
                                    value={`R$ ${parseFloat(salesMetrics.averageTicket || 0).toLocaleString(
                                        "pt-BR",
                                        { minimumFractionDigits: 2 }
                                    )}`}
                                    titleClassName="text-sm font-medium text-gray-700 flex items-center gap-2"
                                    valueClassName={"text-3xl font-bold text-blue-700"}
                                    prefix={<DollarSign className="h-4 w-4 text-purple-600" />}
                                />
                            </CardContent>
                        </Card>
                    </Col>

                    <Col span={24} md={12} lg={8} xl={6}>
                        <Card className="transition-shadow hover:shadow-lg">
                            <CardContent className="p-4">
                                <Statistic
                                    title="ROI"
                                    loading={isFindingSalesMetrics}
                                    value={`${parseFloat(salesMetrics.roi || 0).toLocaleString("pt-BR")}%`}
                                    titleClassName="text-sm font-medium text-gray-700 flex items-center gap-2"
                                    valueClassName={`text-3xl font-bold ${salesMetrics.roi > 0
                                        ? "text-purple-700"
                                        : "text-red-700"
                                        }`}
                                    prefix={<TrendingUp className="h-4 w-4 text-purple-600" />}
                                />
                            </CardContent>
                        </Card>
                    </Col>

                </Row>

                <Row gutter={[12, 12]} className="mt-4">

                    <Col span={12} xs={24} md={6}>
                        <Card>
                            <section style={{ padding: 24 }}>
                                <Typography variant="h4" className="text-lg font-semibold">
                                    Receita por Pacote
                                </Typography>
                                <Typography variant="body1">
                                    Comparação de vendas entre pacotes
                                </Typography>
                            </section>

                            <CardContent className="flex flex-col items-center">
                                <PieChart width={360} height={320}>
                                    <Pie
                                        data={getProductChartData()}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={70}
                                        outerRadius={110}
                                        paddingAngle={3}
                                        dataKey="value"
                                        label={renderPieLabel}
                                        labelLine={false}
                                    >
                                        {getProductChartData().map((entry, index) => (
                                            <Cell key={index} fill={entry.color} />
                                        ))}
                                    </Pie>

                                    <Tooltip
                                        formatter={(value, name, props) => [
                                            `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`,
                                            `${props.payload.name} — ${props.payload.vendas} vendas`
                                        ]}
                                    />
                                </PieChart>

                                <div className="mt-6 space-y-2">
                                    {getProductChartData().map((item, index) => (
                                        <div key={index} className="flex items-center gap-2 text-sm text-gray-700">
                                            <div
                                                style={{ background: item.color }}
                                                className="w-3 h-3 rounded-full"
                                            />
                                            <span className="font-medium">{item.name}</span>
                                            <span className="text-gray-500">— {item.vendas} vendas</span>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>

                        </Card>
                    </Col>

                    <Col span={12} xs={24} md={6}>
                        <Card>
                            <section style={{ padding: 24 }}>
                                <Typography variant="h4" className="text-lg font-semibold">
                                    Vendas por Horário
                                </Typography>

                                <Typography variant="body1" className="text-gray-600">
                                    Melhor período de vendas: <strong>{bestPeriod.start} - {bestPeriod.end}</strong> com <strong>{bestPeriod.total}</strong> vendas
                                </Typography>
                            </section>

                            <CardContent>
                                <div className="overflow-x-auto">
                                    <ResponsiveContainer width="100%" height={300}>
                                        <ComposedChart data={hourlySales}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="hour" />

                                            {/* Eixo Y da Receita (esquerda) */}
                                            <YAxis
                                                yAxisId="left"
                                                label={{ value: "Receita (R$)", angle: -90, position: "insideLeft" }}
                                            />

                                            {/* Eixo Y das Vendas (direita) */}
                                            <YAxis
                                                yAxisId="right"
                                                orientation="right"
                                                label={{ value: "Vendas", angle: 90, position: "insideRight" }}
                                            />

                                            <Tooltip formatter={(value, name) => {
                                                if (name === "Receita")
                                                    return [`R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`, name];
                                                return [value, name];
                                            }} />

                                            <Legend />

                                            {/* Receita em Barra */}
                                            <Bar
                                                dataKey="revenue"
                                                name="Receita"
                                                yAxisId="left"
                                                fill="#3B82F6"
                                            />

                                            {/* Vendas em Linha */}
                                            <Line
                                                dataKey="sales"
                                                name="Vendas"
                                                yAxisId="right"
                                                stroke="#10B981"
                                                strokeWidth={3}
                                                dot={false}
                                                type="monotone"
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>

                                </div>
                            </CardContent>
                        </Card>
                    </Col>

                </Row>

            </div>
        </div>
    );
}
